import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useSocket } from './SocketContext';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const socket = useSocket();
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  });

  // Connetti socket quando l'utente è presente
  useEffect(() => {
    if (user && socket) {
      const token = localStorage.getItem('token');
      if (token) {
        console.log('🔌 Inizializzando socket per user:', user.id);
        const socketInstance = socket.connect(token, false); // false = isUser
        
        // Non serve fare join per gli utenti normali - lo fanno dalle singole pagine
      }
    }
  }, [user, socket]);

  // Funzione per tradurre gli errori HTTP in messaggi user-friendly
  const handleAuthError = (error) => {
    if (error.response) {
      const serverMessage = error.response.data?.message || error.response.data?.error;
      if (serverMessage) {
        throw new Error(serverMessage);
      }

      switch (error.response.status) {
        case 400:
          throw new Error('Username o password non validi');
        case 401:
          throw new Error('Non autorizzato: verifica le tue credenziali');
        case 403:
          throw new Error('Non hai i permessi necessari');
        case 404:
          throw new Error('Account non trovato');
        case 409:
          throw new Error('Username già in uso');
        default:
          throw new Error('Errore durante l\'autenticazione');
      }
    } else if (error.request) {
      throw new Error('Impossibile contattare il server');
    } else {
      throw new Error(error.message || 'Errore durante l\'autenticazione');
    }
  };

  // Funzione per effettuare il login
  const login = async (username, password, useMySQL = true) => {
    try {
      const endpoint = useMySQL ? '/api/mysql/auth/login' : '/api/auth/login';
      const response = await axios.post(endpoint, { username, password });
      const userData = response.data.user;
      const token = response.data.token;
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', token); // Salva il token JWT
      localStorage.setItem('authType', useMySQL ? 'mysql' : 'mongodb');
      return userData;
    } catch (error) {
      handleAuthError(error);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token'); // Rimuovi anche il token
    localStorage.removeItem('authType');
    
    // Disconnetti socket
    if (socket) {
      console.log('🔌 Disconnettendo socket per user');
      socket.disconnect();
    }
  };

  // Funzione per effettuare la registrazione
  const register = async (userData, useMySQL = true) => {
    try {
      const endpoint = useMySQL ? '/api/mysql/auth/register' : '/api/auth/register';
      const response = await axios.post(endpoint, userData);
      return response.data;
    } catch (error) {
      handleAuthError(error);
    }
  };

  // Funzione per aggiornare i cantieri assegnati all'utente
  const updateAssignedCantieri = async (userId, cantieriIds) => {
    try {
      const authType = localStorage.getItem('authType') || 'mysql';
      const endpoint = authType === 'mysql' 
        ? `/api/mysql/auth/users/${userId}/cantieri`
        : `/api/auth/users/${userId}/cantieri`;

      const response = await axios.post(endpoint, {
        cantieriIds
      });
      const updatedUser = response.data.user;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      handleAuthError(error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      register, 
      updateAssignedCantieri,
      loading: false
    }}>
      {children}
    </AuthContext.Provider>
  );
};
